import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchHistory } from '../hooks/useSearchHistory';

interface SearchBarProps {
  className?: string;
  showSuggestions?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ className = '', showSuggestions = true }) => {
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { searchHistory, addToHistory, removeFromHistory } = useSearchHistory();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      addToHistory(query.trim());
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsExpanded(false);
      setShowDropdown(false);
      setQuery('');
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    addToHistory(suggestion);
    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    setIsExpanded(false);
    setShowDropdown(false);
    setQuery('');
  };

  const handleSearchClick = () => {
    setIsExpanded(true);
    setTimeout(() => {
      inputRef.current?.focus();
      if (showSuggestions && searchHistory.length > 0) {
        setShowDropdown(true);
      }
    }, 100);
  };

  const handleInputFocus = () => {
    if (showSuggestions && searchHistory.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    // Delay hiding dropdown to allow clicks on suggestions
    setTimeout(() => {
      if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
        setShowDropdown(false);
        if (!query) {
          setIsExpanded(false);
        }
      }
    }, 150);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsExpanded(false);
        setShowDropdown(false);
        setQuery('');
        inputRef.current?.blur();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="flex items-center">
        <div className={`flex items-center transition-all duration-300 ${
          isExpanded ? 'w-64 bg-black/50 border border-gray-600' : 'w-10'
        } rounded-full`}>
          <button
            type={isExpanded ? 'submit' : 'button'}
            onClick={!isExpanded ? handleSearchClick : undefined}
            className="flex items-center justify-center w-10 h-10 text-gray-300 hover:text-white transition-colors duration-200"
            aria-label="Search"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder="Search movies, shows..."
            className={`bg-transparent text-white placeholder-gray-400 outline-none transition-all duration-300 ${
              isExpanded ? 'w-full pr-4 opacity-100' : 'w-0 opacity-0'
            }`}
          />
        </div>
      </form>

      {/* Search Suggestions Dropdown */}
      {showDropdown && showSuggestions && searchHistory.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
        >
          <div className="p-2">
            <div className="flex items-center justify-between px-3 py-2 text-xs text-gray-400 border-b border-gray-700">
              <span>Recent Searches</span>
              <button
                onClick={() => {
                  searchHistory.forEach(removeFromHistory);
                  setShowDropdown(false);
                }}
                className="hover:text-white transition-colors duration-200"
              >
                Clear All
              </button>
            </div>
            {searchHistory.map((suggestion, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-3 py-2 hover:bg-gray-700 rounded cursor-pointer group"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-white">{suggestion}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromHistory(suggestion);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-white transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;